import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { BetManager } from "../Managers/BetManager";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Paylines } from "../GameComponents/Paylines";
import { TeasingExtended } from "./TeasingExtended";
import { ISymbolTeasingAttributes, SymbolTeasingActions } from "./TeasingComponentAbstract";
import { Symbols } from "../GameComponents/Symbols";
import { Symbol } from "../GameComponents/Symbol";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ModuleManager } from "../Managers/ModuleManager";
import { BaseView } from "../GameComponents/Base/BaseView";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Payline } from "../GameComponents/Payline";
import { PlayList } from "../Components/Sounds/PlayList";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    protected static readonly IdleJack: string = "idleJack";
    private _idleJackStart: FrameTimerEventEmitter;
    private _finishedAnimationDelay: FrameTimerEventEmitter;

    protected _teasingExtended: TeasingExtended;
    protected teasing: SwitchElement;
    protected teasingFS: SwitchElement;
    protected teasingBonus: SwitchElement;
    protected winframes: ContainerElement;
    private singleLine: boolean = false;
    private doubledWinCount: number = 0;
    protected randomMonsterSoundId: number;

    // JACK
    protected idleJackMessageId: number; // idle animation id for message waiting
    protected idleJackMessages: number = 4; // number of idle animation id for message waiting

    constructor() {
        super();

        this._idleJackStart = CreateFrameTimer("idleJackStart");
        this._idleJackStart.on(FrameTimerEvents.Complete, this.idleJack.bind(this));

        this._finishedAnimationDelay = CreateFrameTimer("finishedAnimation");
        this._finishedAnimationDelay.on(FrameTimerEvents.Complete, this.finishedAnimation.bind(this));

        this.randomMonsterSoundId = Math.floor(Math.random() * 7);
    }

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        teasingExtended?: TeasingExtended,
        teasingFS?: SwitchElement,
        teasingBonus?: SwitchElement,
        winframes?: ContainerElement,
    ): void {
        this._teasingExtended = teasingExtended;
        this.teasingFS = teasingFS;
        this.teasingBonus = teasingBonus;
        this.winframes = winframes;
        this.idleJackMessageId = Math.floor(Math.random() * this.idleJackMessages);
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);
    }

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.doubledWinCount = 0;
                for (let i = 0; i < wins.length; i++) {
                    if (wins[i].hasOwnProperty("symbolMultiplier")) {
                        this.doubledWinCount++;
                    }
                }

                if (this.currentMultiplierIndex >= 3) {
                    let animation = this.animations[this.currentMultiplierIndex];
                    let container: ContainerElement = <any>animation.children[0];

                    container.addChildAt(BaseView.Jack, container.children.length - 1 - 4);
                    container.childs["Jack"] = BaseView.Jack;
                } else {
                    BaseView.Jack.visible = true;
                }

                this.showFirstWins();
            }

            BaseView.Jack.visible = true;
            return this.currentMultiplierIndex >= 3;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            BaseView.Jack.visible = true;
            return false;
        }
    }

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();
            this.finishFirstInitWins();
        } else {
            for (let i = 0; i < this.currentSymbols.length; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if ([5, 6, 7, 8, 18, 19, 20, 21].indexOf(this.currentSymbols[i][j].id) != -1) {
                        if (this.currentSymbols[i][j].getCurrentCase() == "NORMAL") {
                            this.currentSymbols[i][j].goToCase("INIT");
                        }
                    }
                }
            }
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
    }

    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        this.cycleWins();
    }

    protected finishFirstInitWins(): void {
        this.currentCycle = 0;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        if (this.currentWins.length == 0) {
            return;
        }

        if (this.singleLine) {
            return;
        }

        this.resetWinFrames();

        let caseSymbol: string = Symbol.CaseCycle;

        if (this.currentWins.length == 1) {
            this.singleLine = true;
            caseSymbol = "SINGLE_WIN";
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.singleLine) {
                this.winPaylineGoToCase(i, caseSymbol);
            } else {
                // this.winPaylineGoToCase(i, 'WIN_HOLD');
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];
                this.noWinSymbolsContainer.addChild(symbol);
            }
        }

        this.winSymbolsGoToCase(this.currentCycle, caseSymbol, true);
        this.winPaylineGoToCase(this.currentCycle, caseSymbol);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    protected showCustomWin(): boolean {
        /**
         * Play monster sounds
         * */
        if (
            this.currentWins.some((value: Communication.Game.IWin) => {
                return [6, 7, 8, 9, 14, 15, 16, 17].indexOf(value.payoutId) != -1;
            })
        ) {
            let tempRandomNumber = this.randomMonsterSoundId;
            this.randomMonsterSoundId = Math.floor(Math.random() * 7);
            while (this.randomMonsterSoundId == tempRandomNumber) {
                this.randomMonsterSoundId = Math.floor(Math.random() * 7);
            }
            PlayList.play("SND_monster" + this.randomMonsterSoundId);
        }

        /**
         * Play wild sounds
         * */

        if (
            this.currentWins.some((value: Communication.Game.IWin) => {
                if (value.trigger !== Communication.Game.WinTrigger.Scatter) {
                    if (ModuleManager.freespinsActive()) {
                        return value.symbols.indexOf(14) != -1;
                    } else {
                        return value.symbols.indexOf(10) != -1;
                    }
                }
                return false;
            })
        ) {
            if (ModuleManager.freespinsActive()) {
                PlayList.play("SND_WildFs");
            } else {
                PlayList.play("SND_WildBase");
            }
        }

        return false;
    }

    public reset(showLastSymbols: boolean, spin = false): boolean {
        for (let i = 0; i < this.winframes.children.length; i++) {
            let winframe: SwitchElement = <any>this.winframes.children[i];
            let addFS: string = ModuleManager.freespinsActive() ? "_FS" : "";
            winframe.goToCase("NORMAL" + addFS);
        }
        PlayList.stop("SND_WildFs");
        PlayList.stop("SND_WildBase");

        this.stopTimer();

        this.currentWins = null;
        this.currentWin = null;

        if (showLastSymbols) {
            if (spin) {
                this.allSymbolsGoToCase(Symbol.CaseRotation, false);
            } else {
                this.allSymbolsGoToReset();
            }
        } else {
            this.allSymbolsGoToCase(Symbol.CaseEmpty, false);
        }

        let addFS: string = BaseView.OutroFrom == "FS" ? "_FS" : "";
        this.allPaylinesGoToCase(Payline.CaseNormal + addFS);

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                return false;
            } else {
                this.stopAnimation();

                return lastMultiplierIndex >= 3;
            }
        } else {
            return lastMultiplierIndex >= 3;
        }
    }

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number, force?: boolean): void {
        this.singleLine = false;

        super.onReelStopped(reelsData, reelIndex);

        let sta: Array<Array<ISymbolTeasingAttributes>>;

        if (this._teasingExtended != null) {
            sta = this._teasingExtended.checkSymbolsPerReel(reelIndex);
        }

        if (!force && !ModuleManager.freespinsActive()) {
            if (this._teasingExtended != null) {
                // if (this._teasingExtended.teasingEnabled){
                sta = this._teasingExtended.checkSymbolsPerReel(reelIndex);
                // }
                for (let i = 0; i <= reelIndex; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        if (this.currentSymbols[i][j] != null) {
                            if (typeof sta !== "undefined") {
                                if (sta[i][j].action === SymbolTeasingActions.NOWIN) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                                    }
                                } else if (sta[i][j].action === SymbolTeasingActions.NORMAL) {
                                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                        if (
                                            [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 18, 19, 20, 21].indexOf(
                                                this.currentSymbols[i][j].id,
                                            ) != -1 &&
                                            reelIndex == i
                                        ) {
                                            if (this.currentSymbols[i][j].id == 10) {
                                                if (
                                                    this._teasingExtended.countSymbols(0, reelIndex, 11) ==
                                                    reelIndex + 1
                                                ) {
                                                    this.currentSymbols[i][j].goToCase(Symbol.CaseRotation + "_STOP");
                                                    // PlayList.play('SND_BaseFSHit');
                                                } else {
                                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                                }
                                            } else if (this.currentSymbols[i][j].id == 11) {
                                                if (
                                                    this._teasingExtended.countSymbols(0, reelIndex, 12) ==
                                                    reelIndex - 1
                                                ) {
                                                    this.currentSymbols[i][j].goToCase(Symbol.CaseRotation + "_STOP");
                                                } else {
                                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                                }
                                            } else {
                                                this.currentSymbols[i][j].goToCase(Symbol.CaseRotation + "_STOP");
                                            }
                                        } else if (
                                            [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 18, 19, 20, 21].indexOf(
                                                this.currentSymbols[i][j].id,
                                            ) == -1 ||
                                            reelIndex == 4 ||
                                            reelIndex == i
                                        ) {
                                            this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                        }
                                    }
                                } else if (sta[i][j].action === SymbolTeasingActions.IMPACT) {
                                    if (this.allowSymbolGoToCase(i, j, "IMPACT")) {
                                        if ([10, 11].indexOf(this.currentSymbols[i][j].id) != -1 && reelIndex == i) {
                                            this.currentSymbols[i][j].goToCase("ROTATION_STOP_IMPACT");
                                        } else {
                                            this.currentSymbols[i][j].goToCase("IMPACT");
                                        }
                                    }
                                } else if (sta[i][j].action === SymbolTeasingActions.TEASE) {
                                    if (this.allowSymbolGoToCase(i, j, "IMPACT")) {
                                        if ([10, 11].indexOf(this.currentSymbols[i][j].id) != -1) {
                                            this.currentSymbols[i][j].goToCase("IMPACT");
                                        }
                                    }
                                }
                            } else {
                                if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                }
                            }
                        }
                    }
                }
            }
        } else {
            for (let i = 0; i <= reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.currentSymbols[i][j] != null) {
                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                            if (!force) {
                                if (
                                    i == reelIndex &&
                                    [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 18, 19, 20, 21].indexOf(
                                        this.currentSymbols[i][j].id,
                                    ) != -1
                                ) {
                                    this.currentSymbols[i][j].goToCase(Symbol.CaseRotation + "_STOP");
                                } else {
                                    if (this.currentSymbols[i][j].id != 13) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                    }
                                }
                            } else {
                                if (
                                    [5, 6, 7, 8, 9, 12, 13, 14, 18, 19, 20, 21].indexOf(this.currentSymbols[i][j].id) !=
                                    -1
                                ) {
                                    if (
                                        reelIndex == 4 &&
                                        this.currentSymbols[i][j].getCurrentCase() != Symbol.CaseRotation + "_STOP"
                                    ) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseRotation + "_STOP");
                                    } else {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                    }
                                } else {
                                    if (this.currentSymbols[i][j].id != 13) {
                                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        let counterFS = 0;
        let counterPM = 0;

        for (let i = 0; i <= reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id == 10) {
                    if (this._teasingExtended.countSymbols(0, reelIndex, 11) == reelIndex + 1) {
                        counterFS++;
                    }
                } else if (this.currentSymbols[i][j].id == 11) {
                    if (this._teasingExtended.countSymbols(0, reelIndex, 12) == reelIndex - 1) {
                        counterPM++;
                    }
                }
            }
        }
        for (let j = 0; j < this.currentSymbols[reelIndex].length; j++) {
            if (this.currentSymbols[reelIndex][j].id == 13 && ModuleManager.freespinsActive()) {
                PlayList.play("SND_FsWildHit");
            }
        }

        if (counterFS != 0) {
            PlayList.play("SND_BaseFSHit0" + counterFS);
        }

        if (counterPM != 0) {
            PlayList.play("SND_BaseBonusHit0" + counterPM);
        }

        if (this.teasingFS != null && reelIndex == 2 && this.teasingFS.getCurrentCase() != "EMPTY") {
            if (ModuleManager.freespinsEnabled()) {
                this.teasingFS.goToCase("WIN");
                PlayList.play("SND_BaseTeaserFsWin");
            } else {
                this.teasingFS.goToCase("FAIL");
                PlayList.play("SND_BaseTeaserFsFail");
            }
        }

        if (this.teasingBonus != null && reelIndex == 4 && this.teasingBonus.getCurrentCase() != "EMPTY") {
            if (ModuleManager.pickEnabled()) {
                this.teasingBonus.goToCase("WIN");
                PlayList.play("SND_BaseTeaserBonusWin");
            } else {
                this.teasingBonus.goToCase("FAIL");
                PlayList.play("SND_BaseTeaserBonusFail");
            }
        } else if (this.teasingBonus != null && reelIndex == 4) {
            if (ModuleManager.pickEnabled()) {
                PlayList.play("SND_BaseTeaserBonusWin");
            }
        }
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (BaseView.PaylinesContainer != null) {
            if ([6, 7, 8, 9, 14, 15, 16, 17].indexOf(this.currentWins[winIndex].payoutId) != -1) {
                let tempRandomNumber = this.randomMonsterSoundId;
                this.randomMonsterSoundId = Math.floor(Math.random() * 7);
                while (this.randomMonsterSoundId == tempRandomNumber) {
                    this.randomMonsterSoundId = Math.floor(Math.random() * 7);
                }

                PlayList.play("SND_monster" + this.randomMonsterSoundId);
            }

            if (
                this.currentWins[winIndex].symbols.indexOf(10) != -1 ||
                this.currentWins[winIndex].symbols.indexOf(14) != -1
            ) {
                if (ModuleManager.freespinsActive()) {
                    PlayList.play("SND_WildFs");
                } else {
                    PlayList.play("SND_WildBase");
                }
            }

            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                BaseView.PaylinesContainer.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];
                    let delayedTimes = [0, 10, 20, 30, 40];
                    let txt: string = "WinFrame" + i + definition[i];
                    let winframe: SwitchElement = this.winframes.childs[txt];

                    if (caseType == Symbol.CaseWin || caseType == Symbol.CaseCycle || caseType == "SINGLE_WIN") {
                        symbol.setDynamicTime(delayedTimes[i]);
                        symbol.goToCase(caseType + "_DELAYED");
                    } else {
                        symbol.goToCase(caseType);
                    }

                    let addFS: string = ModuleManager.freespinsActive() ? "_FS" : "";

                    if (!ModuleManager.freespinsActive()) {
                        if (this.currentWins.length == this.doubledWinCount) {
                            addFS = "_X";
                        } else {
                            if (caseType == Symbol.CaseCycle && win.hasOwnProperty("symbolMultiplier")) {
                                addFS = "_X";
                            }
                        }
                    }

                    winframe.goToCase(caseType + addFS);

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    let delayedTimes = [0, 10, 20, 30, 40];
                    let txt: string = "WinFrame" + i + j;
                    let winframe: SwitchElement = this.winframes.childs[txt];

                    if (caseType == Symbol.CaseWin || caseType == Symbol.CaseCycle || caseType == "SINGLE_WIN") {
                        symbol.setDynamicTime(delayedTimes[i]);
                        symbol.goToCase(caseType + "_DELAYED");
                    } else {
                        symbol.goToCase(caseType);
                    }

                    let addFS: string = ModuleManager.freespinsActive() ? "_FS" : "";

                    if (!ModuleManager.freespinsActive()) {
                        if (this.currentWins.length == this.doubledWinCount) {
                            addFS = "_X";
                        } else {
                            if (caseType == Symbol.CaseCycle && win.hasOwnProperty("symbolMultiplier")) {
                                addFS = "_X";
                            }
                        }
                    }

                    winframe.goToCase(caseType + addFS);
                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        PlayList.play("SND_BaseScatterWin");

        return symbols;
    }

    protected allPaylinesGoToCase(caseType: string): void {
        if (BaseView.PaylinesContainer != null) {
            if (ModuleManager.freespinsActive()) caseType = caseType + "_FS";
            BaseView.PaylinesContainer.goToCase(caseType);
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (BaseView.PaylinesContainer != null) {
            const win = this.currentWins[winIndex];

            let addFS: string = ModuleManager.freespinsActive() ? "_FS" : "";

            if (!ModuleManager.freespinsActive()) {
                if (this.currentWins.length == this.doubledWinCount) {
                    addFS = "_X";
                } else {
                    if (caseType == Symbol.CaseCycle && win.hasOwnProperty("symbolMultiplier")) {
                        addFS = "_X";
                    } else if (
                        (caseType == Payline.CaseWin || caseType.indexOf("WIN_FINISHED") != -1) &&
                        win.hasOwnProperty("symbolMultiplier")
                    ) {
                        addFS = "_X_GREEN";
                    }
                }
            }

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle && this.currentWins.length === 1) {
                    caseType = Payline.CaseSingleCycle + "_" + win.count + addFS;
                    BaseView.PaylinesContainer.goToCase(caseType, win.paylineId);
                } else if (caseType === Payline.CaseCycle) {
                    caseType = Payline.CaseCycle + "_" + win.count + addFS;
                    BaseView.PaylinesContainer.goToCase(caseType, win.paylineId);
                } else if (caseType === "SINGLE_WIN") {
                    caseType = "SINGLE_WIN_" + win.count + addFS;
                    BaseView.PaylinesContainer.goToCase(caseType, win.paylineId);
                } else if (caseType == Payline.CaseWin) {
                    caseType = Payline.CaseWin + "_" + win.count + addFS;
                    BaseView.PaylinesContainer.goToCase(caseType, win.paylineId);
                } else {
                    BaseView.PaylinesContainer.goToCase(caseType + addFS, win.paylineId);
                }
            }
        }
    }

    protected onAnimationFinished(): void {
        if (this.currentWins != null && this.currentWins.length > 1) {
            if (this.getAnimationIndex() != 0) {
                for (let i = 0; i < this.currentWins.length; i++) {
                    // this.winSymbolsGoToCase(i, caseSymbol, false);
                    let caseSymbol: string = "WIN_FINISHED_" + (this.getAnimationIndex() > 2 ? "HIGH" : "LOW");

                    this.winPaylineGoToCase(i, caseSymbol + "_" + this.currentWins[i].count);
                    this.hideWinFrames(caseSymbol);
                }
            } else {
                this._finishedAnimationDelay.start(60);
            }
        }

        super.onAnimationFinished();
    }

    protected finishedAnimation(): void {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                // this.winSymbolsGoToCase(i, caseSymbol, false);
                let caseSymbol: string = "WIN_FINISHED_" + (this.getAnimationIndex() > 2 ? "HIGH" : "LOW");

                this.winPaylineGoToCase(i, caseSymbol + "_" + this.currentWins[i].count);
                this.hideWinFrames(caseSymbol);
            }
        }
    }

    public getAnimationIndex(): number {
        if (
            this.currentWins.length === 1 &&
            this.currentWins[0].trigger === Communication.Game.WinTrigger.Scatter &&
            this.currentWin === 0
        ) {
            return 6;
        } else {
            return super.getAnimationIndex();
        }
    }

    protected hideWinFrames(caseType: string): void {
        for (let i = 0; i < this.winframes.children.length; i++) {
            let winframe: SwitchElement = <any>this.winframes.children[i];
            if (winframe.getCurrentCase().indexOf("WIN") != -1) {
                if (winframe.getCurrentCase().indexOf("_FS") != -1) {
                    winframe.goToCase(caseType + "_FS");
                } else if (winframe.getCurrentCase().indexOf("_X") != -1) {
                    winframe.goToCase(caseType + "_X");
                } else {
                    winframe.goToCase(caseType);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol.getCurrentCase() === Symbol.CaseNormal || symbol.getCurrentCase() === "INIT") {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    protected idleJack(): void {
        // 3 main and idle animations for messages

        let actualIdleStep: number = Math.floor(Math.random() * this.idleJackMessages);
        while (this.idleJackMessageId == actualIdleStep) {
            actualIdleStep = Math.floor(Math.random() * this.idleJackMessages);
        }

        this.idleJackMessageId = actualIdleStep;

        BaseView.Jack.goToCase("IDLE_MESSAGE_" + this.idleJackMessageId);
        this._idleJackStart.start(480);
    }

    public startIdleJack(): void {
        this._idleJackStart.start(480);
    }

    protected getIdleActiveElementsCount(): number {
        return 1;
    }

    protected getIdleDelay(): number {
        return 60;
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimator.Idle:
                this.idle();
                break;
            case WinAnimator.CycleWins:
                this.cycleWins();
                break;
            case WinAnimatorExtended.IdleJack:
                this.idleJack();
                break;
        }
    }

    public stopTimer(): void {
        if (this._idleJackStart != null) {
            this._idleJackStart.stop();
        }

        if (this.timer != null) {
            this.timer.stop();
        }
    }

    resetWinFrames(): void {
        for (let i = 0; i < this.winframes.children.length; i++) {
            let winframes: SwitchElement = <any>this.winframes.children[i];
            let addFS: string = ModuleManager.freespinsActive() ? "_FS" : "";
            winframes.goToCase("NORMAL" + addFS);
        }
    }

    setDynamicTimeOnSymbol(reelIndex: number, symbolIndex: number, time: number): void {
        this.currentSymbols[reelIndex][symbolIndex].setDynamicTime(time - 45);
        this.currentSymbols[reelIndex][symbolIndex].goToCase("WAIT");
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/